import React, { useMemo, useState } from 'react';
import { Box, Popover, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { TOKENS } from '../board/ganttConfig';
import { listInkForecastRuns, InkForecastRun } from '../productionApi';
import { productionKeys } from '../queryKeys';
import { ForecastProjection, fmtSheets } from './inkForecastModel';

/**
 * Which projections the report is built from.
 *
 * Ink is many-to-many, so an order quantity is only right once every customer's
 * demand for that ink is in the total — the default is therefore the newest
 * projection for EVERY customer, and narrowing it is the deliberate act, not
 * widening it.
 *
 * The picker's real job is not selection, it is DISCLOSURE. A forecast built on
 * a projection taken two years ago is a materially different number from one
 * built on this week's, and this is the only place that is visible before
 * someone acts on it. Hence age on every row, and the sheets a stale projection
 * is contributing to months that have already been run.
 */

const MONO = TOKENS.monoFont;
const RULE = TOKENS.line;

/** A projection is stale once it is older than the weekly import cadence by a
 *  clear margin — a month means several refreshes have been missed. */
const STALE_DAYS = 30;

export type Selection = string[] | null;   // null = server default (newest per customer)

export const pairKey = (code: string, customer: string) => `${code}:${customer}`;

/**
 * How old the DATA is, from the projection code — not from imported_at.
 *
 * imported_at is when we happened to load the PDF, which says nothing about
 * currency: loading a 2024 projection this morning would read as "0 days old",
 * which is precisely backwards. The projection code IS the date the account
 * manager took the forecast, and that is the thing a planner needs to judge.
 */
const ageDays = (projectionCode: string): number | null => {
  if (!/^\d{8}$/.test(projectionCode)) return null;
  const taken = Date.UTC(
    Number(projectionCode.slice(0, 4)),
    Number(projectionCode.slice(4, 6)) - 1,
    Number(projectionCode.slice(6, 8)),
  );
  return Math.floor((Date.now() - taken) / 86_400_000);
};

interface Props {
  /** What the server actually built this report from — the effective selection. */
  active: ForecastProjection[];
  selection: Selection;
  onChange: (next: Selection) => void;
}

export default function ProjectionPicker({ active, selection, onChange }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const { data: runs = [] } = useQuery({
    queryKey: productionKeys.inkForecastRuns(),
    queryFn: listInkForecastRuns,
    staleTime: 5 * 60_000,
  });

  /** Every projection we hold, newest first, grouped by customer. */
  const byCustomer = useMemo(() => {
    const m = new Map<string, InkForecastRun[]>();
    runs.forEach((r) => {
      const list = m.get(r.customer_code) ?? [];
      list.push(r);
      m.set(r.customer_code, list);
    });
    m.forEach((list) => list.sort((a, b) => b.projection_code.localeCompare(a.projection_code)));
    return [...Array.from(m.entries())].sort((a, b) => a[0].localeCompare(b[0]));
  }, [runs]);

  /** customer → the pair currently in force, from the report itself. */
  const activeByCustomer = useMemo(() => {
    const m = new Map<string, string>();
    active.forEach((p) => m.set(p.customer, pairKey(p.code, p.customer)));
    return m;
  }, [active]);

  /** Dropped-sheet warnings, keyed by pair, straight off the built report. */
  const droppedByPair = useMemo(() => {
    const m = new Map<string, number>();
    active.forEach((p) => m.set(pairKey(p.code, p.customer), p.dropped_sheets_before_horizon));
    return m;
  }, [active]);

  // The picker edits an EXPLICIT list. While selection is null the report is on
  // the server default, so seed from what it actually used — otherwise the first
  // click would silently change more than the row that was clicked.
  const effective = useMemo<string[]>(
    () => selection ?? Array.from(activeByCustomer.values()),
    [selection, activeByCustomer],
  );

  const chosenFor = (customer: string): string | undefined =>
    effective.find((p) => p.endsWith(`:${customer}`));

  const setCustomer = (customer: string, pair: string | null) => {
    const rest = effective.filter((p) => !p.endsWith(`:${customer}`));
    onChange(pair ? [...rest, pair].sort() : rest);
  };

  const label = selection === null
    ? `${active.length} customer${active.length === 1 ? '' : 's'}`
    : `${effective.length} of ${byCustomer.length}`;

  const stale = active.filter((p) => (ageDays(p.code) ?? 0) > STALE_DAYS).length;

  return (
    <>
      <Box
        component="button" type="button" onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          font: 'inherit', fontSize: 12, fontWeight: 500, px: '10px', py: '3px',
          borderRadius: `${TOKENS.radiusChip}px`, cursor: 'pointer', whiteSpace: 'nowrap',
          border: `1px solid ${stale ? TOKENS.warn : RULE}`,
          bgcolor: TOKENS.panel, color: stale ? TOKENS.warn : TOKENS.ink2,
          boxShadow: TOKENS.shadowSm,
          '&:hover': { borderColor: TOKENS.accent, color: TOKENS.accent },
        }}
      >
        {label}{stale > 0 ? ` · ${stale} stale` : ''} ▾
      </Box>

      <Popover
        open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: '8px', boxShadow: TOKENS.shadowSm,
          border: `1px solid ${RULE}` } } }}
      >
        <Box sx={{ minWidth: 620, bgcolor: TOKENS.panel }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
            borderBottom: `1px solid ${RULE}`,
          }}>
            <Box sx={{
              fontSize: 11, fontWeight: 600, letterSpacing: '0.5px',
              textTransform: 'uppercase', color: TOKENS.ink3, mr: 'auto',
            }}>
              Projections
            </Box>
            {([
              ['Newest for all', () => onChange(null)],
              ['All', () => onChange(byCustomer.map(([c, list]) => pairKey(list[0].projection_code, c)).sort())],
              ['None', () => onChange([])],
            ] as [string, () => void][]).map(([t, fn]) => (
              <Box key={t} component="button" type="button" onClick={fn} sx={{
                font: 'inherit', fontSize: 11, px: '8px', py: '2px', cursor: 'pointer',
                borderRadius: `${TOKENS.radiusChip}px`, border: `1px solid ${RULE}`,
                bgcolor: TOKENS.panel, color: TOKENS.ink2,
                '&:hover': { borderColor: TOKENS.accent, color: TOKENS.accent },
              }}>
                {t}
              </Box>
            ))}
          </Box>

          <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
            {byCustomer.map(([customer, list]) => {
              const chosen = chosenFor(customer);
              const on = !!chosen;
              const run = list.find((r) => pairKey(r.projection_code, r.customer_code) === chosen)
                ?? list[0];
              const age = ageDays(run.projection_code);
              const dropped = chosen ? droppedByPair.get(chosen) ?? 0 : 0;
              return (
                <Box key={customer} sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 1.5, height: 26,
                  borderBottom: `1px solid ${TOKENS.lineSoft}`, fontSize: 11.5,
                  bgcolor: on ? 'transparent' : 'rgba(38,32,27,0.03)',
                  color: on ? TOKENS.ink : TOKENS.ink3,
                }}>
                  <Box
                    component="input" type="checkbox" checked={on}
                    onChange={() => setCustomer(customer,
                      on ? null : pairKey(list[0].projection_code, customer))}
                    sx={{ m: 0, cursor: 'pointer', accentColor: TOKENS.accent }}
                  />
                  <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700, width: 52 }}>
                    {customer}
                  </Box>

                  {/* Only offer a chooser where there is something to choose. */}
                  {list.length > 1 ? (
                    <Box
                      component="select" value={chosen ?? pairKey(list[0].projection_code, customer)}
                      disabled={!on}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setCustomer(customer, e.target.value)}
                      sx={{
                        font: 'inherit', fontFamily: MONO, fontSize: 11, width: 108,
                        border: `1px solid ${RULE}`, borderRadius: '4px',
                        bgcolor: TOKENS.panel, color: 'inherit', cursor: 'pointer',
                      }}
                    >
                      {list.map((r) => (
                        <option key={r.id} value={pairKey(r.projection_code, r.customer_code)}>
                          {r.projection_code}
                        </option>
                      ))}
                    </Box>
                  ) : (
                    <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, width: 108 }}>
                      {run.projection_code}
                    </Box>
                  )}

                  <Box component="span" sx={{
                    fontFamily: MONO, fontSize: 11, width: 56, textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    color: age !== null && age > STALE_DAYS ? TOKENS.warn : 'inherit',
                    fontWeight: age !== null && age > STALE_DAYS ? 700 : 400,
                  }}>
                    {age === null ? '—' : age >= 365 ? `${(age / 365).toFixed(1)} y` : `${age} d`}
                  </Box>
                  <Box component="span" sx={{
                    fontFamily: MONO, fontSize: 11, width: 74, textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums', color: TOKENS.ink3,
                  }}>
                    {run.demand_line_count} lines
                  </Box>

                  {/* The sharpest staleness signal we have: not "this is old" but
                      "this projection is demonstrably describing months we have
                      already run". */}
                  {dropped > 0 && (
                    <Tooltip title={
                      `${fmtSheets(dropped)} sheets of this projection fall before the `
                      + 'shared horizon and are excluded from every figure on the report'
                    }>
                      <Box component="span" sx={{
                        ml: 'auto', fontSize: 10.5, color: TOKENS.warn, cursor: 'help',
                        whiteSpace: 'nowrap',
                      }}>
                        ⚠ {fmtSheets(dropped)} sheets already run
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box sx={{
            px: 1.5, py: 0.75, borderTop: `1px solid ${RULE}`,
            fontSize: 11, color: TOKENS.ink3,
          }}>
            {effective.length} of {byCustomer.length} customers selected
            {selection === null && ' · newest for each (default)'}
          </Box>
        </Box>
      </Popover>
    </>
  );
}
